// Sidebar — CRM left rail with responsive layout, mobile Drawer support, and clean tooltips.
// Expanded mode renders NAV_GROUPS as collapsible submenus; the collapsed icon
// rail shows one icon per group whose popover lists the group items.
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Drawer, Popover, Tooltip } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { useFeatureFlag } from '@govard/feature-flags/react';
import { ChevronDown, ChevronLeft, Grid, X } from 'lucide-react';

import { CRM_FLAG_IDS } from '../../feature-flags/index.js';
import { isLinkActive, type NavGroup, type NavItem } from './nav-items.js';
import { useSavedViews } from '../../features/views/hooks/useSavedViews.js';
import { useCurrentScope, useIsPlatformAdmin } from '../../shared/scope/useCurrentScope.js';
import { useNavigationPreferencesStore } from '../../features/navigation/navigationPreferencesStore.js';

export interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps): ReactElement {
  const { t } = useTranslation();
  const proMode = useFeatureFlag(CRM_FLAG_IDS.PRO_MODE);
  const runCenterEnabled = useFeatureFlag(CRM_FLAG_IDS.RUN_CENTER);
  const [isExpanded, setIsExpanded] = useState(false);
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(() => new Set());
  /** Key of the group whose popover is open in the collapsed rail. */
  const [openGroupPopover, setOpenGroupPopover] = useState<string | null>(null);
  const location = useLocation();
  const scope = useCurrentScope();
  const isPlatformAdmin = useIsPlatformAdmin();

  const savedViews = useSavedViews();
  const pinnedViews = useMemo(
    () => (savedViews.data ?? []).filter((view) => view.inSidebar),
    [savedViews.data],
  );

  const canSeeOwnerAdminItems = scope !== null && ['owner', 'admin'].includes(scope.role);

  // User navigation preferences (Settings → «Меню»): group/item order and
  // hidden items apply to every sidebar surface — expanded, collapsed rail
  // and the mobile drawer. Role/flag filtering runs after, so preferences
  // never unlock anything the user could not see anyway.
  const orderedNavGroups = useNavigationPreferencesStore((state) => state.orderedGroups);

  /** Groups with items that survive the current flags, role, and admin rights. */
  const visibleGroups = useMemo(
    () =>
      orderedNavGroups.map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            (item.pro !== true || proMode) &&
            (item.runCenter !== true || runCenterEnabled) &&
            (item.ownerAdminOnly !== true || canSeeOwnerAdminItems) &&
            (item.platformAdminOnly !== true || isPlatformAdmin),
        ),
      })),
    [orderedNavGroups, proMode, runCenterEnabled, canSeeOwnerAdminItems, isPlatformAdmin],
  ).filter((group) => group.items.length > 0);

  const activeGroupKey = useMemo(() => {
    const active = visibleGroups.find((group) =>
      group.items.some((item) => isLinkActive(location.pathname, item.to)),
    );
    return active?.key ?? null;
  }, [visibleGroups, location.pathname]);

  // Keep the group containing the active route expanded so the current page
  // stays reachable after navigation.
  useEffect(() => {
    if (activeGroupKey === null) return;
    setOpenGroups((prev) => (prev.has(activeGroupKey) ? prev : new Set(prev).add(activeGroupKey)));
  }, [activeGroupKey]);

  // Toggling the rail size swaps group popovers for expanded sections: clear
  // stale popover state so re-collapsing never re-opens a popup on its own.
  useEffect(() => {
    setOpenGroupPopover(null);
  }, [isExpanded]);

  const toggleGroup = (key: string): void => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const renderNavContent = (expanded: boolean, isMobile: boolean = false) => {
    /** Labeled item row used by expanded groups and collapsed group popovers. */
    const renderNavLink = (item: NavItem, onNavigate?: () => void) => {
      const Icon = item.icon;
      const active = isLinkActive(location.pathname, item.to);
      const label = t(item.labelKey);
      const tooltipTitle = item.badge ? `${label} (${item.badge})` : label;
      const handleNavigate: (() => void) | undefined =
        isMobile || onNavigate !== undefined
          ? () => {
              if (isMobile) onMobileClose?.();
              onNavigate?.();
            }
          : undefined;

      return (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/dashboard'}
          aria-label={tooltipTitle}
          onClick={handleNavigate}
          className={({ isActive }) =>
            `app-nav-link flex items-center gap-3 rounded-md px-3 py-2 text-[13px] transition-all duration-150 active:scale-95 ${
              isActive
                ? 'bg-amber-50 font-semibold text-[var(--ant-color-primary)]'
                : 'text-[var(--ant-color-text-secondary)] hover:bg-stone-100 hover:text-[var(--ant-color-text)]'
            }`
          }
        >
          <Icon size={18} strokeWidth={active ? 2.2 : 1.75} className="shrink-0" />
          <div className="flex min-w-0 flex-1 items-center justify-between gap-1.5">
            <span className="truncate">{label}</span>
            {item.badge ? (
              <span className="rounded px-1.5 py-0.5 text-[9px] font-bold tracking-wider uppercase bg-amber-500/10 text-amber-700 dark:text-amber-400 dark:bg-amber-500/20">
                {item.badge}
              </span>
            ) : null}
          </div>
        </NavLink>
      );
    };

    /** Placeholder items: disabled row with a coming-soon tooltip (no navigation). */
    const renderDisabledItem = (item: NavItem) => {
      const Icon = item.icon;
      const label = t(item.labelKey);
      const comingSoon = t('crm.nav.coming_soon');

      return (
        <Tooltip key={item.to} title={comingSoon} placement="right" mouseEnterDelay={0.2}>
          <div
            className="flex items-center gap-3 rounded-md px-3 py-2 text-[13px] text-[var(--ant-color-text-disabled)] cursor-not-allowed"
            aria-disabled="true"
          >
            <Icon size={18} strokeWidth={1.75} className="shrink-0 opacity-50" />
            <span className="truncate opacity-50">{label}</span>
          </div>
        </Tooltip>
      );
    };

    const renderNavItem = (item: NavItem, onNavigate?: () => void) =>
      item.comingSoon === true ? renderDisabledItem(item) : renderNavLink(item, onNavigate);

    /** Collapsible group header + indented children (expanded sidebar only). */
    const renderNavGroup = (group: NavGroup) => {
      const GroupIcon = group.icon;
      const groupLabel = t(group.labelKey);
      const groupActive = group.items.some((item) => isLinkActive(location.pathname, item.to));
      const isOpen = openGroups.has(group.key);
      // Desktop rail and mobile drawer both render this list: keep the
      // aria-controls target unique per instance.
      const listId = `nav-group-${group.key}${isMobile ? '-mobile' : ''}`;

      return (
        <div key={group.key}>
          <button
            type="button"
            onClick={() => toggleGroup(group.key)}
            aria-expanded={isOpen}
            aria-controls={listId}
            className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-[13px] transition-all duration-150 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500 ${
              groupActive
                ? 'font-semibold text-[var(--ant-color-primary)]'
                : 'font-medium text-[var(--ant-color-text-secondary)] hover:bg-stone-100 hover:text-[var(--ant-color-text)]'
            }`}
          >
            <GroupIcon size={18} strokeWidth={groupActive ? 2.2 : 1.75} className="shrink-0" />
            <span className="min-w-0 flex-1 truncate text-left">{groupLabel}</span>
            <ChevronDown
              size={14}
              strokeWidth={1.75}
              className={`shrink-0 text-[var(--ant-color-text-tertiary)] transition-transform duration-150 ${
                isOpen ? '' : '-rotate-90'
              }`}
            />
          </button>
          {isOpen ? (
            <div id={listId} className="mt-0.5 ml-6 space-y-1 border-l border-stone-200/70 pl-2">
              {group.items.map((item) => renderNavItem(item))}
            </div>
          ) : null}
        </div>
      );
    };

    /** Collapsed rail: one icon per group; a popover lists the group items. */
    const renderCollapsedGroup = (group: NavGroup) => {
      const GroupIcon = group.icon;
      const groupLabel = t(group.labelKey);
      const groupActive = group.items.some((item) => isLinkActive(location.pathname, item.to));
      const closeGroupPopover = (): void => setOpenGroupPopover(null);

      return (
        <Popover
          key={group.key}
          placement="right"
          align={{ offset: [8, 0] }}
          trigger={['hover', 'click']}
          open={openGroupPopover === group.key}
          onOpenChange={(open) => setOpenGroupPopover(open ? group.key : null)}
          title={groupLabel}
          content={
            <div className="min-w-[200px] space-y-1 py-1">
              {group.items.map((item) => renderNavItem(item, closeGroupPopover))}
            </div>
          }
        >
          <button
            type="button"
            aria-label={groupLabel}
            aria-haspopup="menu"
            aria-expanded={openGroupPopover === group.key}
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md aria-expanded:bg-amber-50 aria-expanded:text-[var(--ant-color-primary)] transition-all duration-150 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500 ${
              groupActive
                ? 'border border-amber-200 bg-amber-50 font-semibold text-[var(--ant-color-primary)]'
                : 'text-[var(--ant-color-text-tertiary)] hover:bg-stone-100 hover:text-[var(--ant-color-text-secondary)]'
            }`}
          >
            <GroupIcon size={20} strokeWidth={1.75} className="shrink-0" />
          </button>
        </Popover>
      );
    };

    return (
      <div className="flex h-full flex-col justify-between">
        {/* Header with Logo and Expand/Collapse toggle */}
        <div
          className={`flex h-[64px] shrink-0 items-center border-b ${
            expanded ? 'justify-between px-4' : 'justify-center px-0'
          }`}
          style={{ borderBottom: '1px solid var(--ant-color-border)' }}
        >
          {expanded ? (
            <>
              <div className="flex items-center gap-3 overflow-hidden">
                <img
                  src="/aicrm-logo-favicon.svg"
                  alt=""
                  aria-hidden="true"
                  className="h-8 w-8 shrink-0 object-contain"
                />
                <span className="truncate text-sm font-bold tracking-tight text-[var(--ant-color-text,#292524)]">
                  GovardOS
                </span>
              </div>

              {isMobile ? (
                <button
                  type="button"
                  onClick={onMobileClose}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-[var(--ant-color-text-tertiary)] hover:bg-stone-100 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
                  aria-label={t('crm.sidebar.close')}
                >
                  <X size={18} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsExpanded(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-[var(--ant-color-text-tertiary)] transition-colors hover:bg-amber-50 hover:text-amber-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
                  aria-label={t('crm.sidebar.collapse')}
                  title={t('crm.sidebar.collapse')}
                >
                  <ChevronLeft size={18} />
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="flex h-10 w-10 items-center justify-center rounded-md transition-all duration-150 active:scale-95 hover:bg-amber-50 group focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
              aria-label={t('crm.sidebar.expand')}
              title={t('crm.sidebar.expand')}
            >
              <img
                src="/aicrm-logo-favicon.svg"
                alt=""
                aria-hidden="true"
                className="h-8 w-8 shrink-0 object-contain transition-transform group-hover:scale-105"
              />
            </button>
          )}
        </div>

        {/* Navigation List */}
        <div
          className={`hide-scrollbar flex-1 overflow-y-auto overflow-x-hidden ${expanded ? 'p-3 space-y-1' : 'flex flex-col items-center gap-2 px-4 py-3'}`}
        >
          {expanded
            ? visibleGroups.map((group) => renderNavGroup(group))
            : visibleGroups.map((group) => renderCollapsedGroup(group))}

          {pinnedViews.length > 0 ? (
            <>
              <div className={`${expanded ? '' : 'w-10'} my-3 border-t border-stone-200/70`} />
              {expanded ? (
                <p className="px-3 pt-1 pb-1 text-[10px] font-bold tracking-widest text-[var(--ant-color-text-tertiary)] uppercase">
                  {t('crm.nav.views').toUpperCase()}
                </p>
              ) : null}
              {pinnedViews.map((view) => {
                const to = `/views/${view.id}`;
                const active = location.pathname === to;
                const linkContent = (
                  <NavLink
                    to={to}
                    onClick={isMobile ? onMobileClose : undefined}
                    className={({ isActive }) =>
                      `app-nav-link flex items-center gap-3 rounded-md transition-all duration-150 active:scale-95 ${
                        expanded
                          ? `px-3 py-2 text-[13px] ${
                              isActive
                                ? 'bg-amber-50 font-semibold text-[var(--ant-color-primary)]'
                                : 'text-[var(--ant-color-text-secondary)] hover:bg-stone-100 hover:text-[var(--ant-color-text)]'
                            }`
                          : `h-10 w-10 shrink-0 justify-center ${
                              isActive
                                ? 'border border-amber-200 bg-amber-50 font-semibold text-[var(--ant-color-primary)]'
                                : 'text-[var(--ant-color-text-tertiary)] hover:bg-stone-100 hover:text-[var(--ant-color-text-secondary)]'
                            }`
                      }`
                    }
                  >
                    <Grid
                      size={expanded ? 18 : 20}
                      strokeWidth={active && expanded ? 2.2 : 1.75}
                      className="shrink-0"
                    />
                    {expanded ? <span className="truncate">{view.title}</span> : null}
                  </NavLink>
                );

                if (!expanded) {
                  return (
                    <Tooltip
                      key={view.id}
                      title={view.title}
                      placement="right"
                      align={{ offset: [8, 0] }}
                      mouseEnterDelay={0.2}
                    >
                      <div className="flex justify-center">{linkContent}</div>
                    </Tooltip>
                  );
                }

                return <div key={view.id}>{linkContent}</div>;
              })}
            </>
          ) : null}
        </div>

        {/* User profile footer */}
        <div
          className={`w-full shrink-0 border-t px-4 py-3 ${
            expanded ? 'flex items-center justify-between gap-3' : 'flex justify-center'
          }`}
          style={{ borderTop: '1px solid var(--ant-color-border)', minHeight: 64 }}
        >
          <Tooltip
            title={expanded ? undefined : t('crm.sidebar.admin_profile')}
            placement="right"
            align={{ offset: [8, 0] }}
          >
            <button
              type="button"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl font-bold text-amber-700 hover:bg-amber-500/[0.14] dark:hover:bg-amber-500/20"
              style={{
                backgroundColor: 'var(--ant-color-primary-bg, #fffbeb)',
                fontSize: 12,
                border: '1px solid var(--ant-color-primary-border, #fbbf24)',
              }}
            >
              AD
            </button>
          </Tooltip>
          {expanded ? (
            <div className="flex flex-1 flex-col overflow-hidden">
              <span className="truncate text-[13px] font-semibold text-[var(--ant-color-text,#292524)]">
                {t('crm.sidebar.admin_name')}
              </span>
              <span className="truncate text-xs text-[var(--ant-color-text-tertiary)]">
                {t('crm.sidebar.system_owner')}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Desktop Sidebar Rail (< 768px hidden on mobile Drawer) */}
      <aside
        aria-label={t('crm.sidebar.brand')}
        className="hidden md:flex h-full shrink-0 flex-col border-r transition-all duration-300 ease-in-out"
        style={{
          width: isExpanded ? 'var(--sidebar-width, 250px)' : '72px',
          backgroundColor: 'var(--crm-sider-bg, #f5f1eb)',
          borderRight: '1px solid var(--ant-color-border)',
        }}
      >
        {renderNavContent(isExpanded, false)}
      </aside>

      {/* Mobile Drawer (screens < 768px) */}
      <Drawer
        placement="left"
        open={mobileOpen}
        onClose={onMobileClose ?? (() => undefined)}
        size={270}
        closeIcon={null}
        // Explicit layer contract: the drawer must stay above every shell
        // layer (topbar z-10, system agent panel z-40, floating buttons) so
        // the open menu is always the single topmost overlay.
        zIndex={1000}
        styles={{
          body: {
            padding: 0,
            backgroundColor: 'var(--ant-color-bg-container, #fffdfa)',
          },
        }}
      >
        {renderNavContent(true, true)}
      </Drawer>
    </>
  );
}
